// Тип Telegram WebApp SDK (window.Telegram.WebApp). Держим здесь, а не в глобальном
// .d.ts, чтобы standalone-typecheck shared-ui (который тянет этот модуль через
// use-telegram-login → telegram-bot) видел типы без web-глобалов.
export type TelegramInsets = {
  top: number
  right: number
  bottom: number
  left: number
}

export type TelegramHapticFeedback = {
  impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
  notificationOccurred: (type: 'error' | 'success' | 'warning') => void
  selectionChanged: () => void
}

export type TelegramBackButton = {
  isVisible: boolean
  show: () => void
  hide: () => void
  onClick: (callback: () => void) => void
  offClick: (callback: () => void) => void
}

export type TelegramWebApp = {
  initData: string
  initDataUnsafe: {
    start_param?: string
    user?: {
      id: number
      first_name?: string
      last_name?: string
      username?: string
      // Язык интерфейса Telegram у пользователя
      language_code?: string
    }
  }
  platform: string
  version: string
  colorScheme?: 'light' | 'dark'
  isActive?: boolean
  isFullscreen: boolean
  viewportHeight?: number
  viewportStableHeight?: number
  safeAreaInset?: TelegramInsets
  contentSafeAreaInset?: TelegramInsets
  HapticFeedback?: TelegramHapticFeedback
  BackButton?: TelegramBackButton
  ready: () => void
  expand: () => void
  disableVerticalSwipes?: () => void
  enableClosingConfirmation?: () => void
  disableClosingConfirmation?: () => void
  requestFullscreen?: () => void
  exitFullscreen?: () => void
  setHeaderColor?: (color: string) => void
  setBackgroundColor?: (color: string) => void
  setBottomBarColor?: (color: string) => void
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void
  openTelegramLink: (url: string) => void
  readTextFromClipboard?: (callback?: (text: string | null) => void) => void
  // viewportChanged получает { isStateStable }: во время анимации разворачивания приходит серия false
  onEvent: (eventType: string, callback: (event?: { isStateStable?: boolean }) => void) => void
  offEvent?: (eventType: string, callback: (event?: { isStateStable?: boolean }) => void) => void
}

type WindowWithTelegram = Window & {
  Telegram?: {
    WebApp?: TelegramWebApp
  }
}

function getWindowTelegram(): TelegramWebApp | undefined {
  if (typeof window === 'undefined') {
    return undefined
  }

  return (window as WindowWithTelegram).Telegram?.WebApp
}

export const isMobile = (): boolean =>
  typeof navigator !== 'undefined' &&
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

export const isTMA = (): boolean => {
  const platform = getWindowTelegram()?.platform
  return Boolean(platform && platform !== 'unknown')
}

export const isTMAAndroid = (): boolean => getWindowTelegram()?.platform === 'android'

export const isTMAIOS = (): boolean => getWindowTelegram()?.platform === 'ios'

export const isTMADesktop = (): boolean => {
  const platform = getWindowTelegram()?.platform
  return platform === 'tdesktop' || platform === 'macos' || platform === 'windows'
}

export const getTelegramWebApp = (): TelegramWebApp | undefined => getWindowTelegram()

export const isTMAFullscreen = (): boolean => getWindowTelegram()?.isFullscreen === true

// Чтение буфера через Telegram WebApp API: WebView (особенно Android) не даёт
// navigator.clipboard.readText. Telegram может не вызвать callback вовсе
// (старая версия клиента / запуск не из attachment menu) — страхуемся таймаутом.
export const readTmaClipboardText = (): Promise<string> =>
  new Promise((resolve) => {
    const webApp = getWindowTelegram()

    if (!webApp?.readTextFromClipboard) {
      resolve('')
      return
    }

    const timer = setTimeout(() => resolve(''), 1000)

    try {
      webApp.readTextFromClipboard((text) => {
        clearTimeout(timer)
        resolve(text ?? '')
      })
    } catch {
      clearTimeout(timer)
      resolve('')
    }
  })

export const getTmaTopInset = (): number => {
  const webApp = getWindowTelegram()

  if (!webApp?.isFullscreen) return 0

  const safeTop = webApp.safeAreaInset?.top ?? 0
  const contentTop = webApp.contentSafeAreaInset?.top ?? 0

  return safeTop + contentTop
}

export const getTmaBottomInset = (): number => {
  const webApp = getWindowTelegram()

  if (!webApp) return 0

  const safeBottom = webApp.safeAreaInset?.bottom ?? 0
  const contentBottom = webApp.contentSafeAreaInset?.bottom ?? 0

  // Fullscreen: как getTmaTopInset — system + Telegram chrome (не max: иначе таббар режется).
  if (webApp.isFullscreen) {
    return safeBottom + contentBottom
  }

  // До fullscreen viewport уже под шапкой TG, но home indicator всё ещё перекрывает таббар.
  return safeBottom
}
